import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Package, Tags, ShoppingCart, LogOut, Plus, Pencil, Trash2,
  X, Upload, Search, TrendingUp, AlertTriangle, IndianRupee, Boxes, Loader2,
} from "lucide-react";
import { api, imgUrl, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const EMPTY = {
  name: "", description: "", category: "T-Shirts", price: "", discountPrice: "",
  sizes: [], stock: "", sku: "", images: [],
  isNewArrival: false, isFeatured: false, isPublished: true, isSoldOut: false,
};
const ALL_SIZES = ["S", "M", "L", "XL", "XXL", "30", "32", "34", "36", "Free Size"];
const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="glass p-6" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
    <div className={`w-10 h-10 flex items-center justify-center mb-4 ${accent}`}><Icon className="w-5 h-5" /></div>
    <p className="font-display font-black text-3xl">{value}</p>
    <p className="font-mono-th text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">{label}</p>
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 border border-white/10 px-4 py-3 cursor-pointer" data-testid={testid}>
    <span className="text-sm text-neutral-300">{label}</span>
    <div onClick={(e) => { e.preventDefault(); onChange(!checked); }} className={`w-10 h-5 rounded-full transition-colors relative ${checked ? "bg-emerald-500" : "bg-neutral-700"}`}>
      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${checked ? "left-5" : "left-0.5"}`} />
    </div>
  </label>
);

function ProductForm({ initial, categories, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSize = (s) =>
    setForm((f) => ({ ...f, sizes: f.sizes.includes(s) ? f.sizes.filter((x) => x !== s) : [...f.sizes, s] }));

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
      toast.success(`${urls.length} image(s) uploaded`);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!form.name || !form.price || !form.category) {
      toast.error("Name, price and category are required");
      return;
    }
    if (!form.images.length) {
      toast.error("Add at least one image");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      discountPrice: form.discountPrice ? Number(form.discountPrice) : null,
      stock: Number(form.stock) || 0,
    };
    try {
      if (initial?.id) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(initial?.id ? "Product updated" : "Product created");
      onSaved();
      onClose();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="product-form-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative glass w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 md:p-8">
        <button onClick={onClose} className="absolute top-5 right-5" data-testid="product-form-close"><X className="w-5 h-5" /></button>
        <h2 className="font-display font-bold text-2xl uppercase mb-6">{initial?.id ? "Edit Product" : "Add New Product"}</h2>

        <div className="space-y-4">
          <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Product name" className="w-full bg-[#13151A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-white/30" data-testid="form-name" />
          <textarea value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={3} className="w-full bg-[#13151A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-white/30 resize-none" data-testid="form-description" />

          <div className="grid grid-cols-2 gap-4">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="bg-[#13151A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-white/30" data-testid="form-category">
              {categories.map((c) => <option key={c.id} value={c.name} className="bg-[#13151A]">{c.name}</option>)}
            </select>
            <input value={form.sku} onChange={(e) => set("sku", e.target.value)} placeholder="SKU (auto if blank)" className="bg-[#13151A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-white/30" data-testid="form-sku" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Price ₹" className="bg-[#13151A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-white/30" data-testid="form-price" />
            <input type="number" value={form.discountPrice} onChange={(e) => set("discountPrice", e.target.value)} placeholder="Discount ₹" className="bg-[#13151A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-white/30" data-testid="form-discount" />
            <input type="number" value={form.stock} onChange={(e) => set("stock", e.target.value)} placeholder="Stock qty" className="bg-[#13151A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-white/30" data-testid="form-stock" />
          </div>

          <div>
            <p className="font-mono-th text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Sizes</p>
            <div className="flex flex-wrap gap-2">
              {ALL_SIZES.map((s) => (
                <button key={s} onClick={() => toggleSize(s)} className={`min-w-9 h-9 px-2 border font-mono-th text-xs ${form.sizes.includes(s) ? "bg-white text-black border-white" : "border-white/15 text-neutral-400"}`} data-testid={`form-size-${s}`}>{s}</button>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono-th text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Images</p>
            <div className="flex flex-wrap gap-3">
              {form.images.map((im, i) => (
                <div key={i} className="relative w-20 h-24 group">
                  <img src={imgUrl(im)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => set("images", form.images.filter((_, x) => x !== i))} className="absolute -top-2 -right-2 bg-rose-500 rounded-full p-1" data-testid={`form-remove-image-${i}`}><X className="w-3 h-3" /></button>
                </div>
              ))}
              <label className="w-20 h-24 border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-white/40 text-neutral-500" data-testid="form-upload-label">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                <span className="text-[9px] mt-1 font-mono-th">UPLOAD</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} data-testid="form-upload-input" />
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Toggle label="New Arrival" checked={form.isNewArrival} onChange={(v) => set("isNewArrival", v)} testid="form-toggle-new" />
            <Toggle label="Featured" checked={form.isFeatured} onChange={(v) => set("isFeatured", v)} testid="form-toggle-featured" />
            <Toggle label="Published" checked={form.isPublished} onChange={(v) => set("isPublished", v)} testid="form-toggle-published" />
            <Toggle label="Sold Out" checked={form.isSoldOut} onChange={(v) => set("isSoldOut", v)} testid="form-toggle-soldout" />
          </div>

          <button onClick={save} disabled={saving} className="w-full bg-white text-black py-4 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold disabled:opacity-50 mt-2" data-testid="form-save-btn">
            {saving ? "Saving..." : initial?.id ? "Update Product" : "Create Product"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", image: "" });

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin/login");
  }, [user, loading, navigate]);

  const loadStats = useCallback(() => api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}), []);
  const loadProducts = useCallback(() => {
    const qs = search ? `?search=${encodeURIComponent(search)}` : "";
    api.get(`/admin/products${qs}`).then(({ data }) => setProducts(data)).catch(() => {});
  }, [search]);
  const loadCategories = useCallback(() => api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {}), []);
  const loadOrders = useCallback(() => api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {}), []);

  useEffect(() => {
    if (user?.role === "admin") {
      loadStats(); loadProducts(); loadCategories(); loadOrders();
    }
  }, [user, loadStats, loadProducts, loadCategories, loadOrders]);

  useEffect(() => { if (user?.role === "admin") loadProducts(); }, [search, user, loadProducts]);

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    loadProducts(); loadStats();
  };

  const addCategory = async () => {
    if (!newCat.name) return toast.error("Category name required");
    await api.post("/admin/categories", newCat);
    toast.success("Category added");
    setNewCat({ name: "", image: "" });
    loadCategories();
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/admin/categories/${id}`);
    toast.success("Category deleted");
    loadCategories();
  };

  const updateOrderStatus = async (id, status) => {
    await api.put(`/orders/${id}/status`, { status });
    toast.success("Order updated");
    loadOrders(); loadStats();
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const NAV = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "products", label: "Products", icon: Package },
    { id: "categories", label: "Categories", icon: Tags },
    { id: "orders", label: "Orders", icon: ShoppingCart },
  ];

  return (
    <div className="min-h-screen flex flex-col md:flex-row" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="md:w-64 border-b md:border-b-0 md:border-r border-white/10 md:min-h-screen flex md:flex-col shrink-0">
        <div className="p-6 hidden md:block">
          <p className="font-display font-black tracking-[0.15em] text-lg">THRIFT<span className="text-neutral-600">HOUSE</span></p>
          <p className="font-mono-th text-[10px] uppercase tracking-[0.2em] text-neutral-600 mt-1">Admin Panel</p>
        </div>
        <nav className="flex md:flex-col flex-1 md:p-4 overflow-x-auto">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => setTab(n.id)} className={`flex items-center gap-3 px-5 md:px-4 py-4 md:py-3 font-mono-th text-xs uppercase tracking-[0.15em] whitespace-nowrap transition-colors ${tab === n.id ? "text-white md:bg-white/5" : "text-neutral-500 hover:text-neutral-300"}`} data-testid={`admin-tab-${n.id}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-3 px-5 md:px-4 py-4 md:py-3 md:m-4 font-mono-th text-xs uppercase tracking-[0.15em] text-neutral-500 hover:text-rose-400" data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4" /> <span className="hidden md:inline">Logout</span>
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-5 md:p-10 overflow-x-hidden">
        {tab === "overview" && (
          <div data-testid="admin-overview">
            <h1 className="font-display font-black text-3xl md:text-4xl uppercase mb-8">Dashboard</h1>
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={IndianRupee} label="Revenue" value={formatPrice(stats.revenue)} accent="text-emerald-400 bg-emerald-500/10" />
                <StatCard icon={ShoppingCart} label="Total Orders" value={stats.totalOrders} accent="text-sky-400 bg-sky-500/10" />
                <StatCard icon={TrendingUp} label="Pending Orders" value={stats.pendingOrders} accent="text-amber-400 bg-amber-500/10" />
                <StatCard icon={Package} label="Products" value={stats.totalProducts} accent="text-white bg-white/10" />
                <StatCard icon={Boxes} label="Published" value={stats.publishedProducts} accent="text-emerald-400 bg-emerald-500/10" />
                <StatCard icon={AlertTriangle} label="Low Stock" value={stats.lowStock} accent="text-rose-400 bg-rose-500/10" />
                <StatCard icon={X} label="Sold Out" value={stats.soldOutProducts} accent="text-rose-400 bg-rose-500/10" />
              </div>
            )}
          </div>
        )}

        {tab === "products" && (
          <div data-testid="admin-products">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <h1 className="font-display font-black text-3xl md:text-4xl uppercase">Products</h1>
              <button onClick={() => { setEditing(null); setShowForm(true); }} className="inline-flex items-center gap-2 bg-white text-black px-5 py-3 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold" data-testid="add-product-btn">
                <Plus className="w-4 h-4" /> Add Product
              </button>
            </div>
            <div className="relative mb-6 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products..." className="w-full bg-[#13151A] border border-white/10 pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-white/30" data-testid="admin-product-search" />
            </div>
            <div className="glass overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="font-mono-th text-[10px] uppercase tracking-widest text-neutral-500 border-b border-white/10">
                    <th className="text-left p-4">Product</th><th className="text-left p-4">Category</th><th className="text-left p-4">Price</th><th className="text-left p-4">Stock</th><th className="text-left p-4">Status</th><th className="text-right p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-product-row-${p.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={imgUrl(p.image)} alt="" className="w-10 h-12 object-cover bg-[#1A1D24]" />
                          <div className="min-w-0"><p className="font-medium line-clamp-1">{p.name}</p><p className="font-mono-th text-[10px] text-neutral-600">{p.sku}</p></div>
                        </div>
                      </td>
                      <td className="p-4 text-neutral-400">{p.category}</td>
                      <td className="p-4">{formatPrice(p.discountPrice || p.price)}</td>
                      <td className="p-4 text-neutral-400">{p.stock}</td>
                      <td className="p-4">
                        <div className="flex flex-wrap gap-1">
                          {p.isSoldOut && <span className="text-[9px] font-mono-th uppercase bg-rose-500/15 text-rose-400 px-1.5 py-0.5">Sold Out</span>}
                          {!p.isPublished && <span className="text-[9px] font-mono-th uppercase bg-neutral-500/15 text-neutral-400 px-1.5 py-0.5">Draft</span>}
                          {p.isPublished && !p.isSoldOut && <span className="text-[9px] font-mono-th uppercase bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5">Live</span>}
                          {p.isNewArrival && <span className="text-[9px] font-mono-th uppercase bg-white/10 text-white px-1.5 py-0.5">New</span>}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => { setEditing(p); setShowForm(true); }} className="p-2 hover:bg-white/10" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => deleteProduct(p.id)} className="p-2 hover:bg-rose-500/20 text-rose-400" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {products.length === 0 && <p className="p-8 text-center text-neutral-500 text-sm">No products found.</p>}
            </div>
          </div>
        )}

        {tab === "categories" && (
          <div data-testid="admin-categories">
            <h1 className="font-display font-black text-3xl md:text-4xl uppercase mb-8">Categories</h1>
            <div className="glass p-6 mb-8 flex flex-col sm:flex-row gap-3 max-w-2xl">
              <input value={newCat.name} onChange={(e) => setNewCat((c) => ({ ...c, name: e.target.value }))} placeholder="Category name" className="flex-1 bg-[#13151A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-white/30" data-testid="new-category-name" />
              <input value={newCat.image} onChange={(e) => setNewCat((c) => ({ ...c, image: e.target.value }))} placeholder="Image URL (optional)" className="flex-1 bg-[#13151A] border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-white/30" data-testid="new-category-image" />
              <button onClick={addCategory} className="bg-white text-black px-5 py-3 font-mono-th text-xs uppercase tracking-[0.2em] font-semibold" data-testid="add-category-btn">Add</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories.map((c) => (
                <div key={c.id} className="glass p-4 flex items-center justify-between" data-testid={`admin-category-${c.id}`}>
                  <span className="font-display font-medium">{c.name}</span>
                  <button onClick={() => deleteCategory(c.id)} className="text-rose-400 p-1" data-testid={`delete-category-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="admin-orders">
            <h1 className="font-display font-black text-3xl md:text-4xl uppercase mb-8">Orders</h1>
            <div className="space-y-4">
              {orders.map((o) => (
                <div key={o.id} className="glass p-5" data-testid={`admin-order-${o.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <p className="font-mono-th text-sm">{o.orderNumber}</p>
                      <p className="text-neutral-400 text-sm mt-1">{o.customerName} · {o.phone}</p>
                      <p className="text-neutral-600 text-xs mt-1 max-w-md">{o.address}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-display font-bold text-lg">{formatPrice(o.total)}</span>
                      <select value={o.status} onChange={(e) => updateOrderStatus(o.id, e.target.value)} className="bg-[#13151A] border border-white/10 px-3 py-2 text-xs font-mono-th uppercase focus:outline-none focus:border-white/30" data-testid={`order-status-${o.id}`}>
                        {STATUSES.map((s) => <option key={s} value={s} className="bg-[#13151A]">{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/10 flex flex-wrap gap-3">
                    {o.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-neutral-400">
                        <img src={imgUrl(it.image)} alt="" className="w-8 h-10 object-cover bg-[#1A1D24]" />
                        {it.name} <span className="text-neutral-600">×{it.quantity} ({it.size})</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {orders.length === 0 && <p className="text-neutral-500 text-sm">No orders yet.</p>}
            </div>
          </div>
        )}
      </main>

      <AnimatePresence>
        {showForm && (
          <ProductForm initial={editing} categories={categories} onClose={() => setShowForm(false)} onSaved={() => { loadProducts(); loadStats(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}
